import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ESCAPE_PRIORITY, useEscapeAction } from '@peruselab/structure-editor/components';
import { Icon } from './Icon';
import { SvgMarkup } from './SvgMarkup';

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.25;
const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/** The picture's own size: the SVG's width and height, at the standard bond length. */
function naturalSize(svg: string): { width: number; height: number } {
  const width = Number(/<svg[^>]*\swidth="([\d.]+)"/.exec(svg)?.[1] ?? 0);
  const height = Number(/<svg[^>]*\sheight="([\d.]+)"/.exec(svg)?.[1] ?? 0);
  return { width: width || 240, height: height || 200 };
}

/** #984 -- what the dialog shows: an exported SVG, loaded when it opens (a molecule, a drawing),
 *  or a picture the card has already drawn (a reaction scheme), with its own size. */
export type ZoomPicture =
  | { readonly kind: 'svg'; readonly load: () => Promise<string> }
  | { readonly kind: 'drawn'; readonly width: number; readonly height: number; readonly content: ReactNode };

interface StructureZoomDialogProps {
  readonly picture: ZoomPicture;
  readonly name: string;
  /** How it matched, e.g. "Substructure: 0.03". */
  readonly matchLabel?: ReactNode;
  /** The molecule's Monograph, when the picture is a molecule. */
  readonly monographHref?: string | undefined;
  /** Actions rendered alongside the zoom tools (e.g. Add to paper). */
  readonly actions?: ReactNode;
  readonly onClose: () => void;
}

/**
 * #963 (owner, 2026-10-13): a result's structure picture, enlarged -- for a large molecule whose
 * card is too small to show where the match is. It opens at the standard bond length (the
 * paper's), in a box no larger than about 90% of the screen, scrolled so the match is in the
 * middle. Zoom with − / +, Fit (the whole structure) and 100%, Ctrl/⌘ + wheel (a trackpad pinch)
 * or a two-finger pinch; scroll or drag one finger to move around. Close with X, Escape or a
 * click outside. #984 -- every result card's picture opens it: molecules, reactions, drawings.
 */
export function StructureZoomDialog({
  picture,
  name,
  matchLabel,
  monographHref,
  actions,
  onClose
}: StructureZoomDialogProps) {
  const { t } = useTranslation();
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const viewRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const pinch = useRef<{ pointers: Map<number, { x: number; y: number }>; distance: number; zoom: number }>({
    pointers: new Map(),
    distance: 0,
    zoom: 1
  });

  useEffect(() => {
    if (picture.kind !== 'svg') return;
    let cancelled = false;
    picture
      .load()
      .then((markup) => {
        if (!cancelled) setSvg(markup);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [picture]);

  // Focus moves into the dialog, and back to what opened it when it closes.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => opener?.focus();
  }, []);

  useEscapeAction(
    ESCAPE_PRIORITY.overlay,
    () => {
      onClose();
      return true;
    },
    true
  );

  const size =
    picture.kind === 'drawn' ? { width: picture.width, height: picture.height } : svg ? naturalSize(svg) : null;

  // Keep the match (or, without one, the middle of the structure) in the middle of the view.
  useLayoutEffect(() => {
    const view = viewRef.current;
    const canvas = canvasRef.current;
    if (!view || !canvas || !size) return;
    const canvasBox = canvas.getBoundingClientRect();
    const band = canvas.querySelector('.match-highlight')?.getBoundingClientRect();
    const centreX = band && band.width > 0 ? band.left + band.width / 2 - canvasBox.left : canvasBox.width / 2;
    const centreY = band && band.height > 0 ? band.top + band.height / 2 - canvasBox.top : canvasBox.height / 2;
    view.scrollLeft = centreX - view.clientWidth / 2;
    view.scrollTop = centreY - view.clientHeight / 2;
  }, [svg, picture, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const fit = useCallback(() => {
    const view = viewRef.current;
    if (!view || !size) return;
    // The most room the view can take: its own maximum size, as the stylesheet sets it.
    const style = getComputedStyle(view);
    const room = (value: string, fallback: number) => (value.endsWith('px') ? parseFloat(value) : fallback);
    // Inside its borders, so a fitted picture never scrolls.
    const borders = 2 * (parseFloat(style.borderLeftWidth) || 0) + 2;
    const width = room(style.maxWidth, window.innerWidth * 0.9) - borders;
    const height = room(style.maxHeight, window.innerHeight * 0.75) - borders;
    setZoom(clampZoom(Math.min(width / size.width, height / size.height)));
  }, [size]);

  const dialog = (
    <div
      className="structure-zoom-backdrop"
      // A press here belongs to the dialog, not to the card it was opened from (React events
      // bubble through the portal): it must never start dragging the card.
      onPointerDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="structure-zoom" role="dialog" aria-modal="true" aria-label={t('structureZoom.label', { name })}>
        <div className="structure-zoom-header">
          <h2 dir="auto" title={name}>
            {name}
          </h2>
          {matchLabel ? <span className="structure-zoom-match">{matchLabel}</span> : null}
          <button
            ref={closeRef}
            type="button"
            className="icon-button structure-zoom-close"
            aria-label={t('structureZoom.close')}
            title={t('structureZoom.close')}
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="structure-zoom-tools" role="toolbar" aria-label={t('structureZoom.tools')}>
          <button
            type="button"
            className="icon-button"
            aria-label={t('structureZoom.zoomOut')}
            title={t('structureZoom.zoomOut')}
            disabled={zoom <= MIN_ZOOM}
            onClick={() => setZoom((current) => clampZoom(current / ZOOM_STEP))}
          >
            <Icon name="remove" />
          </button>
          {/* The zoom level; choosing it goes back to the standard bond length (100%). */}
          <button
            type="button"
            className="structure-zoom-level"
            title={t('structureZoom.actualSize')}
            aria-live="polite"
            onClick={() => setZoom(1)}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('structureZoom.zoomIn')}
            title={t('structureZoom.zoomIn')}
            disabled={zoom >= MAX_ZOOM}
            onClick={() => setZoom((current) => clampZoom(current * ZOOM_STEP))}
          >
            <Icon name="add" />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('structureZoom.fit')}
            title={t('structureZoom.fit')}
            onClick={fit}
          >
            <Icon name="fitScreen" />
          </button>
          {actions}
          {monographHref ? (
            <Link
              to={monographHref}
              target="_blank"
              rel="noopener"
              className="icon-button structure-zoom-monograph"
              aria-label={t('structureZoom.monograph')}
              title={t('structureZoom.monograph')}
            >
              <Icon name="info" size="1.1rem" />
            </Link>
          ) : null}
        </div>
        <div
          ref={viewRef}
          className="structure-zoom-view"
          data-testid="structure-zoom-view"
          onWheel={(event) => {
            // Ctrl/⌘ + wheel, which is also what a trackpad pinch sends: zoom. A plain wheel scrolls.
            if (!event.ctrlKey && !event.metaKey) return;
            event.preventDefault();
            setZoom((current) => clampZoom(current * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP)));
          }}
          onPointerDown={(event) => {
            if (event.pointerType !== 'touch') return;
            pinch.current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (pinch.current.pointers.size === 2) {
              const [a, b] = [...pinch.current.pointers.values()] as [
                { x: number; y: number },
                { x: number; y: number }
              ];
              pinch.current.distance = Math.hypot(a.x - b.x, a.y - b.y);
              pinch.current.zoom = zoom;
            }
          }}
          onPointerMove={(event) => {
            const pointers = pinch.current.pointers;
            if (!pointers.has(event.pointerId)) return;
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (pointers.size !== 2 || pinch.current.distance === 0) return;
            const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
            setZoom(clampZoom((pinch.current.zoom * Math.hypot(a.x - b.x, a.y - b.y)) / pinch.current.distance));
          }}
          onPointerUp={(event) => {
            pinch.current.pointers.delete(event.pointerId);
            if (pinch.current.pointers.size < 2) pinch.current.distance = 0;
          }}
          onPointerCancel={(event) => {
            pinch.current.pointers.delete(event.pointerId);
            pinch.current.distance = 0;
          }}
        >
          {size && (svg || picture.kind === 'drawn') ? (
            <div
              ref={canvasRef}
              className="structure-zoom-canvas"
              style={{ width: size.width * zoom, height: size.height * zoom }}
            >
              {picture.kind === 'drawn' ? (
                picture.content
              ) : (
                // Generated by drawingDocumentToSvg, as on the card: the same trust level.
                <SvgMarkup className="isomer-thumbnail-svg" svg={svg ?? ''} />
              )}
            </div>
          ) : (
            <p className="structure-zoom-status">{failed ? t('moleculeThumbnail.renderError') : t('common.loading')}</p>
          )}
        </div>
      </div>
    </div>
  );
  return createPortal(dialog, document.body);
}

/**
 * #963 / #984 -- a result card's picture as the button that opens it larger: click or tap to
 * enlarge; a press-and-drag on it still drags the card.
 */
export function ZoomablePicture({
  name,
  onOpen,
  children
}: {
  readonly name: string;
  readonly onOpen: () => void;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="search-result-zoom"
      data-drags-card
      aria-label={t('structureZoom.open', { name })}
      title={t('structureZoom.open', { name })}
      onClick={onOpen}
    >
      {children}
      <span className="search-result-zoom-hint" aria-hidden="true">
        <Icon name="zoomIn" size="1rem" />
      </span>
    </button>
  );
}
